<template>
  <component :is="component" v-if="component" v-bind="props" />
  <div v-else>
    <div v-for="(titleKey, index) in titleKeys" :key="index">
      <Heading v-if="te(titleKey)">
        <i18n-t :keypath="titleKey" tag="strong" />
      </Heading>
    </div>
    <div v-for="(messageKey, index) in messageKeys" :key="index">
      <i18n-t v-if="te(messageKey)" :keypath="messageKey">
        <template #referenceNumber>
          <strong>{{ referenceNumber }}</strong>
        </template>
        <template #bankName>
          <LenderBankName v-if="providerKey" :provider-key="providerKey" />
        </template>
        <template #bankNameAbbreviation>
          <LenderBankName
            v-if="providerKey"
            :provider-key="providerKey"
            display-type="abbreviation"
          />
        </template>
      </i18n-t>
    </div>
    <div v-for="(disclosureKey, index) in disclosureKeys" :key="index">
      <i18n-t
        v-if="te(disclosureKey)"
        :keypath="disclosureKey"
        tag="small"
        class="block pt-4"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ApplyDecisionGenericProps } from "@/models/apply/apply-models"
  import { useApplyDecisionViewType } from "@/composition/apply/use-apply-decision-view-type"
  import { useApplyDecisionMessageKeys } from "@/composition/apply/use-apply-decision-i18n"
  import { LenderBankName } from "@versatile-credit-apply/components"
  import { computed } from "vue"
  import Heading from "@/components/content/Heading.vue"
  import { useProviderI18n } from "@versatile-credit-apply/sdk"

  const props = defineProps<ApplyDecisionGenericProps>()

  const { te } = useProviderI18n(() => props.response?.providerKey)

  const viewType = useApplyDecisionViewType(props)
  const messagePrefix = computed(() => `apply.responses.${props.decision}`)
  const providerKey = computed(() => props.response?.providerKey)
  const titleKeys = useApplyDecisionMessageKeys(
    viewType,
    messagePrefix,
    "title"
  )
  const messageKeys = useApplyDecisionMessageKeys(
    viewType,
    messagePrefix,
    "message"
  )
  const disclosureKeys = useApplyDecisionMessageKeys(
    viewType,
    messagePrefix,
    "disclosure"
  )
  const referenceNumber = computed(() => props.response?.applicationId)
</script>

<i18n lang="yaml" locale="aff_standard_en">
apply:
  responses:
    declined:
      heading: Complete
      message: >
        We are unable to approve your application at this time. You will receive a written explanation within 7-10
        days regarding the reasons for the decision and the source of the information utilized. It will include a
        number for you to call if you have questions.
</i18n>

<i18n lang="yaml" locale="proceed_standard_en">
apply:
  responses:
    declined:
      heading: Complete
      message: >
        We are unable to approve your application for Proceed Finance.
      title: We were not able to find you an offer at this time
</i18n>

<i18n lang="yaml" locale="fortiva_standard_en">
apply:
  responses:
    declined:
      message: |
        Fortiva Retail Credit is unable to approve your application at this time. You will receive a written explanation
        in the mail within 7-10 days regarding the reasons for the decision and the source of the information utilized.
        It will include a number for you to call if you have questions.
      title: We were not able to find you an offer at this time
</i18n>

<i18n lang="yaml" locale="genesis_standard_en">
apply:
  responses:
    declined:
      message: |
        Unfortunately, {bankName} is unable to approve your application for a Concora™ Credit Account. A letter will
        be sent to the address on your application with additional details.
      title: |
        {strong}Your application is not approved{strong}
</i18n>
